import CLoader from "components/CLoader";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PrivateLabel } from "typedef/PrivateLabel";
import API from "utils/API";
import checkIpOrLocalhost from "utils/checkIpOrLocalhost";
import { useIsHomebridge } from "utils/useIsHomebridge";
import homebridgeIcon from "../../assets/Icons/homebridge_favicon.ico";
import { useMetaTags } from "utils/useMetaTags";

interface PrivateLabelContextType {
  privateLabel: PrivateLabel | undefined;
}
type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: (...args: unknown[]) => void;
};
declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}
export const PrivateLabelContext = createContext<
  PrivateLabelContextType | undefined
>(undefined);

export const UsePrivateLabelContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [privateLabel, setPrivateLabel] = useState<PrivateLabel>();
  const { isHomeBridge } = useIsHomebridge();
  const { updateFaviconAndMeta, updateMetaTags } = useMetaTags();
  const fetchData = async (hostname: string) => {
    try {
      const response = await API.get<PrivateLabel>({
        url: `/get-unsec/private-label/${hostname}`,
      });
      if ("error" in response) {
        alert(response.error);
      } else {
        const privateLabel = response.data;
        setPrivateLabel(privateLabel);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    //const hostname = window.location.hostname;
    //used for test privateLabel
    const hostname = checkIpOrLocalhost(window.location.hostname)
      ? (process.env.REACT_APP_PRIVATE_LABEL_DOMAIN as string)
      : window.location.hostname;
    fetchData(hostname);
  }, []);

  useEffect(() => {
    const includeBorrowerPortalWidget =
      privateLabel?.zendesk?.includeBorrowerPortalWidget;
    const borrowerPortalWidgetUrl =
      privateLabel?.zendesk?.borrowerPortalWidgetUrl;
    const currentHostname = window.location.hostname;
    const isLocalhost = checkIpOrLocalhost(currentHostname);
    const pathName = window.location.pathname;
    if (
      !isLocalhost &&
      includeBorrowerPortalWidget &&
      borrowerPortalWidgetUrl &&
      !pathName.includes("impersonate")
    ) {
      if (!document.getElementById("ze-snippet")) {
        const scriptElement = document.createElement("script");
        scriptElement.id = "ze-snippet";
        scriptElement.src = borrowerPortalWidgetUrl;
        document.head.appendChild(scriptElement);
      }
    }
  }, [
    privateLabel?.zendesk?.borrowerPortalWidgetUrl,
    privateLabel?.zendesk?.includeBorrowerPortalWidget,
  ]);
  useEffect(() => {
    if (
      privateLabel?.meta?.hasBorrowerPortalPixel &&
      privateLabel?.meta?.borrowerPortalPixelId !== 0
    ) {
      (function (
        f: Window & { fbq?: FbqFunction },
        b: Document,
        e: string,
        v: string,
        n?: FbqFunction,
        t?: HTMLScriptElement,
        s?: Node,
      ) {
        if (f.fbq) return;
        n = f.fbq = function (...args: unknown[]) {
          n?.callMethod ? n?.callMethod(...args) : n?.queue?.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js",
      );

      (window as Window & { fbq: FbqFunction }).fbq(
        "init",
        privateLabel.meta.borrowerPortalPixelId,
      );
      (window as Window & { fbq: FbqFunction }).fbq("track", "PageView");

      const noscript = document.createElement("noscript");
      noscript.innerHTML = `
         <img height="1" width="1" style="display:none"
         src="https://www.facebook.com/tr?id=${privateLabel.meta.borrowerPortalPixelId}&ev=PageView&noscript=1" />
       `;
      document.body.appendChild(noscript);
      return () => {
        const pixelScript = document.querySelector(
          'script[src="https://connect.facebook.net/en_US/fbevents.js"]',
        );
        if (pixelScript && pixelScript.parentNode) {
          pixelScript.parentNode.removeChild(pixelScript);
        }
        if (noscript.parentNode) {
          noscript.parentNode.removeChild(noscript);
        }
      };
    }
  }, [privateLabel]);
  const values = useMemo(() => ({ privateLabel }), [privateLabel]);

  if (isLoading) return <CLoader loading={isLoading} />;

  if (isHomeBridge) {
    updateFaviconAndMeta(homebridgeIcon);
  } else if (privateLabel?.mediaResources?.favicon) {
    updateFaviconAndMeta(privateLabel.mediaResources?.favicon);
  }

  if (privateLabel?.mediaResources?.logo) {
    const metaTagsToUpdate = [
      { name: "og:image", content: privateLabel.mediaResources.logo },
      { name: "twitter:image", content: privateLabel.mediaResources.logo },
    ];

    metaTagsToUpdate.forEach((metaTag) => {
      const metaElement =
        document.querySelector(`meta[property='${metaTag.name}']`) ||
        document.querySelector(`meta[name='${metaTag.name}']`);
      if (metaElement) {
        metaElement.setAttribute("content", metaTag.content);
      }
    });
  }

  if (privateLabel?.lenderName) {
    updateMetaTags(`${privateLabel?.lenderName} - Home Equity Loans`);
  }

  if (privateLabel?.frontUrl) {
    const metaTagsToUpdate = [
      { name: "og:url", content: privateLabel.frontUrl },
      { name: "twitter:url", content: privateLabel.frontUrl },
    ];

    metaTagsToUpdate.forEach((metaTag) => {
      const metaElement =
        document.querySelector(`meta[property='${metaTag.name}']`) ||
        document.querySelector(`meta[name='${metaTag.name}']`);
      if (metaElement) {
        metaElement.setAttribute("content", metaTag.content);
      }
    });
  }

  return (
    <PrivateLabelContext.Provider value={values}>
      {children}
    </PrivateLabelContext.Provider>
  );
};

export function usePrivateLabel() {
  const context = useContext(PrivateLabelContext);
  if (!context) {
    throw new Error("PrivateLabelContextProvider not found");
  }
  return context;
}
